import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import {
  ActionBar,
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  Table,
  TableWrap,
  TextArea,
  Title,
  Toast,
} from '../components/ui';

interface CardItem {
  _id: string;
  name: string;
  description: string;
  rarity: string;
  active: boolean;
}

interface TwitchUser {
  _id: string;
  username: string;
  displayName: string;
}

const emptyForm = { name: '', description: '', rarity: 'comun', active: true };

const CardsPage: React.FC = () => {
  const [cards, setCards] = useState<CardItem[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [users, setUsers] = useState<TwitchUser[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignCardId, setAssignCardId] = useState('');
  const [assignQty, setAssignQty] = useState('1');
  const [toast, setToast] = useState('');

  const load = async () => {
    const [cardsRes, usersRes] = await Promise.all([
      api.get('/cards'),
      api.get('/users?limit=100'),
    ]);
    setCards(cardsRes.data);
    setUsers(usersRes.data.users || []);
  };

  useEffect(() => { load(); }, []);

  const createCard = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/cards', form);
    setForm(emptyForm);
    setCreateOpen(false);
    setToast('Carta creada');
    load();
  };

  const toggleCard = async (card: CardItem) => {
    await api.put(`/cards/${card._id}`, { active: !card.active });
    load();
  };

  const assignCard = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/cards/assign', {
      userId: assignUserId,
      cardId: assignCardId,
      quantity: parseInt(assignQty, 10) || 1,
    });
    setAssignQty('1');
    setAssignOpen(false);
    setToast('Carta asignada');
  };

  const unassignCard = async () => {
    await api.post('/cards/unassign', {
      userId: assignUserId,
      cardId: assignCardId,
      quantity: parseInt(assignQty, 10) || 1,
    });
    setAssignOpen(false);
    setToast('Carta quitada');
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Cartas</Title>
          <Subtitle>Crear cartas, asignar inventario y gestionar estados.</Subtitle>
        </PageHeaderMain>
        <PageActions>
          <Button $variant="ghost" type="button" onClick={() => setAssignOpen(true)}>Asignar / Quitar</Button>
          <Button type="button" onClick={() => setCreateOpen(true)}>+ Nueva carta</Button>
        </PageActions>
      </PageHeader>

      {toast && <Toast>{toast}</Toast>}

      <Surface>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th style={{ width: '14%' }}>Rareza</th>
                  <th style={{ width: '12%' }}>Estado</th>
                  <th style={{ width: '14%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c._id}>
                    <td>{c.name}</td>
                    <td>{c.rarity}</td>
                    <td><Badge $active={c.active}>{c.active ? 'Activa' : 'Inactiva'}</Badge></td>
                    <td style={{ textAlign: 'right' }}>
                      <ActionBar>
                        <Button $variant="ghost" $size="sm" type="button" onClick={() => toggleCard(c)}>
                          {c.active ? 'Desactivar' : 'Activar'}
                        </Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
                {!cards.length && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin cartas</td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={createOpen}
        title="Nueva carta"
        onClose={() => { setCreateOpen(false); setForm(emptyForm); }}
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={() => { setCreateOpen(false); setForm(emptyForm); }}>Cancelar</Button>
            <Button type="submit" form="card-create-form">Crear</Button>
          </>
        )}
      >
        <form id="card-create-form" onSubmit={createCard}>
          <FormGrid>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Descripción</Label>
              <TextArea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </FormRow>
            <FormRow>
              <Label>Rareza</Label>
              <Select value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value })}>
                <option value="comun">Común</option>
                <option value="rara">Rara</option>
                <option value="epica">Épica</option>
                <option value="legendaria">Legendaria</option>
              </Select>
            </FormRow>
          </FormGrid>
        </form>
      </Modal>

      <Modal
        open={assignOpen}
        title="Asignar / Quitar carta"
        onClose={() => setAssignOpen(false)}
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={() => setAssignOpen(false)}>Cancelar</Button>
            <Button $variant="danger" type="button" onClick={unassignCard}>Quitar</Button>
            <Button type="submit" form="card-assign-form">Asignar</Button>
          </>
        )}
      >
        <form id="card-assign-form" onSubmit={assignCard}>
          <FormGrid>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Usuario</Label>
              <Select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)} required>
                <option value="">Seleccionar...</option>
                {users.map((u) => <option key={u._id} value={u._id}>{u.displayName} (@{u.username})</option>)}
              </Select>
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Carta</Label>
              <Select value={assignCardId} onChange={(e) => setAssignCardId(e.target.value)} required>
                <option value="">Seleccionar...</option>
                {cards.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
            </FormRow>
            <FormRow>
              <Label>Cantidad</Label>
              <Input type="number" min="1" value={assignQty} onChange={(e) => setAssignQty(e.target.value)} />
            </FormRow>
          </FormGrid>
        </form>
      </Modal>
    </Page>
  );
};

export default CardsPage;
